<template>
  <div ref="wraperCanvas" class="select-img__wrapper">
    <div class="items-on-image-chips-wrapper">
      <Transition v-for="(cropItem, index) in arrayOfImagesParts"  :key="`${index} + chip`">
        <CustomChip
          @click="setCoordinates(cropItem)"
          :label="index+cropItem.width"
          class="fade-item"
          :style="{
                '--delay': `${index * 0.1}s`
              }"
          severity="help"
          :active="cropItem.isSelected"
          >
          <span>{{ index+cropItem.width }}</span>
        </CustomChip>
      </Transition>
    </div>
    <Cropper
      ref="cropper"
      :src="base64String"
      :resizeImage="{ wheel: false }"
      :stencil-props="{
        handlers: {
          eastNorth: true,

          north: false,

          westNorth: true,

          west: false,

          westSouth: true,

          south: false,

          eastSouth: true,

          east: false,
        },
      }"
      :maxWidth="imageWidth"
      :maxHeight="imageHeight"
      :imageRestriction="'stencil'"
      :debounce="1000"
      :defaultVisibleArea="{
        width: imageWidth,
        height: imageHeight,
        left: 0,
        top: 0,
      }"
      :defaultSize="{
        width: imageWidth,
        height: imageHeight,
      }"
      @ready="sendImage(true)"
      @change="saveLastSelectedArea"
    />

    <Transition v-for="(item, index) in arrayOfImagesParts"  :key="`${index} + button`" appear>
    <Button
      @click="setCoordinates(item)"
      severity="secondary"
      class="buttons-cropper-area fade-item"
      :pt="{
        root: {
          style: {
            backgroundColor: 'rgba(216, 229, 173, 0.8)',
            height: '40px',
            width: '40px',
            borderRadius: '50%',
            padding: '0',
          },
        },
      }"
      :style="{
        top: `${item.y + item.height}px`,
        left: `${item.x + item.width}px`,
        '--delay': `${index * 0.1}s`
      }"
    >
      <template #icon>
        <CustomSvgIcons
          name="scope" :size="20"
          class="scope-icon"
        />
      </template>
    </Button>
    </Transition>
    <div v-if="!imageSearch.isImageLoading" class="cropper-load-buttons">
      <transition appear>
        <Button label="Back" class="full-width fade-item"
                severity="secondary"
                outlined  @click="setStep1ForImageFlow"
                :style="{
                  '--delay': '0.1s'
                }"
        />
      </transition>
      <transition appear>
        <Button label="Load"  class="full-width fade-item"
                :loading="isPhotoLoading"  @click="sendImage(false)"
                :style="{
                  '--delay': '0.125s'
                }"
        />
      </transition>
    </div>
  </div>

  <div  v-if="imageSearch.isImageLoading" class="wrapper-for-loading">
    <ProgressSpinner />
  </div>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import ProgressSpinner from 'primevue/progressspinner';

import { ref, watch } from "vue";
import { getImageParts } from "@/api/images";
import type { AxiosResponse } from "axios";
import type { ImagePartItem } from "@/types/ProductType";
import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import { useToast } from 'primevue/usetoast'
import { useSharedStateForComponents, useImageSearch } from '@/features/search/store'
import CustomSvgIcons from '@/components/HelpersComponents/CustomSvgIcons.vue'
import CustomChip from '@/components/UI/CustomChip/CustomChip.vue'
import { useUploadSearchImageMutation } from '@/mutations'
import type { UploadImageResponse } from '@/types/searchItemsTypes'

interface ImagePartsResponse {
  product_image_parts_data: ImagePartItem[];
}

const { currentImageLink, imageWidth, imageHeight } = defineProps<{
  currentImageLink: string;
  imageWidth: number;
  imageHeight: number;
}>();

const emit = defineEmits<{
  (e: "setNewImageLink", newImageLink: string): void;
  (e: 'closeModal'): void;
}>();

const { setStep1ForImageFlow } = useSharedStateForComponents();

const toast = useToast();
const imageSearch = useImageSearch();

const arrayOfImagesParts = ref<ImagePartItem[]>([]);
const base64String = ref<string>("");
interface CropperInstance {
  getResult: () => { canvas: HTMLCanvasElement | null } | null;
  setCoordinates: (updater: () => { left: number; top: number; width: number; height: number }) => void;
}

const cropper = ref<CropperInstance | null>(null);

const workingImageCode = ref<string | null>(null);
const prevResult = ref<string | null>(null);

const lastSelectedImage = ref<File | null>(null);

const isPhotoLoading = ref(false);
const uploadImageMutation = useUploadSearchImageMutation();

const img = new Image();

img.crossOrigin = "Anonymous";
img.src = currentImageLink;
img.onload = function () {
  const targetWidth = imageWidth;
  const targetHeight = imageHeight;

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    console.error("Failed to get canvas 2D context");
    return;
  }

  const scaleX = targetWidth / img.width;
  const scaleY = targetHeight / img.height;

  const scale = Math.min(scaleX, scaleY);

  const drawWidth = img.width * scale;
  const drawHeight = img.height * scale;

  const offsetX = (targetWidth - drawWidth) / 2;
  const offsetY = (targetHeight - drawHeight) / 2;

  ctx.fillStyle = "white";
  ctx.fillRect(0, 0, targetWidth, targetHeight);

  ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

  base64String.value = canvas.toDataURL("image/jpeg");
};

function setCoordinates(item: ImagePartItem) {
  if (!cropper.value || !arrayOfImagesParts.value.length) {
    return;
  }

  cropper.value.setCoordinates(() => ({
    left: item.x,
    top: item.y,
    width: item.width,
    height: item.height
  }));

  arrayOfImagesParts.value.forEach((subItem) => {
    subItem.isSelected = item.y + item.x === subItem.x + subItem.y;
  });
}

async function sendSelectedArea(file: File) {
  isPhotoLoading.value = true;
  const result = await uploadImageMutation.mutateAsync({ file }) as UploadImageResponse;

  if (result?.img_url) {
    workingImageCode.value = result.img_url;
  }
  isPhotoLoading.value = false;
  return result;
}

async function saveLastSelectedArea() {
  if (!cropper.value) return;

  const result = cropper.value.getResult() as { canvas: HTMLCanvasElement | null } | null;

  const resultKey = result ? JSON.stringify(result) : null;
  if (resultKey === prevResult.value) return;
  prevResult.value = resultKey;

  if (result?.canvas) {
    const blob = await new Promise<Blob | null>((resolve) => result.canvas?.toBlob(resolve));
    if (!blob) {
      return;
    }

    lastSelectedImage.value = new File([blob], "imageSource.png", { type: "image/png" });
  }
}

async function sendImage(isFirstLoaded = false) {
  imageSearch.isImageLoading = true;

  try {
    await saveLastSelectedArea();

    if (lastSelectedImage.value) {
      await sendSelectedArea(lastSelectedImage.value);
    }

    if (workingImageCode.value && isFirstLoaded) {
      const res = await getImageParts(workingImageCode.value) as AxiosResponse<ImagePartsResponse>;

      if (res.status === 200) {
        arrayOfImagesParts.value = res.data.product_image_parts_data;
        const firstItem = res.data.product_image_parts_data[0];
        if (firstItem) {
          setCoordinates(firstItem);
        }
      }
    }

    if (!isFirstLoaded) {
      toast.add({ severity: 'info', summary: 'Info', detail: 'Photo is selected', life: 3000 });
      imageSearch.isImageSearchModalOpen = false;
    }
  } finally {
    imageSearch.isImageLoading = false;
  }
}

watch(
  workingImageCode,
  () => {
    if (workingImageCode.value) {
      emit("setNewImageLink", workingImageCode.value);
    }
  },
  {
    deep: true,
  }
);
</script>

<style>

.cropper-load-buttons {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding:20px;
}

.items-on-image-chips-wrapper {
  display: flex;
  justify-content: flex-start;
  gap:8px;
  height: 44px;
  padding-bottom: 6px;
}

.select-img__wrapper {
  position: relative;
  height: 100%;
}

.buttons-cropper-area {
  position: absolute;

  width: 36px;

  height: 36px;

  border-radius: 50%;
}

.start-free-draw-mode {
  bottom: 30px;

  left: 30px;
}

.refresh-image {
  bottom: 30px;

  right: 30px;
}

.canv-cls {
  height: 100%;
}

.vue-simple-handler--east-north {
  display: block;

  background: transparent;

  border: 4px solid rgba(110, 133, 203, 1);

  width: 24px;

  height: 24px;

  border-top-right-radius: 8px;

  border-left: none;

  border-bottom: none;

  transform: translate(-35%, 44%);
}

.vue-simple-handler--east-south {
  display: block;

  background: transparent;

  border: 4px solid rgba(110, 133, 203, 1);

  width: 24px;

  height: 24px;

  border-bottom-right-radius: 8px;

  border-left: none;

  border-top: none;

  transform: translate(-35%, -44%);
}

.vue-simple-handler--west-north {
  display: block;

  background: transparent;

  border: 4px solid rgba(110, 133, 203, 1);

  width: 24px;

  height: 24px;

  border-top-left-radius: 8px;

  border-right: none;

  border-bottom: none;

  transform: translate(35%, 44%);
}

.vue-simple-handler--west-south {
  display: block;

  background: transparent;

  border: 4px solid rgba(110, 133, 203, 1);

  width: 24px;

  height: 24px;

  border-bottom-left-radius: 8px;

  border-right: none;

  border-top: none;

  transform: translate(35%, -44%);
}

.vue-simple-line {
  border-color: transparent !important;
}

.scope-icon {
  color: var(--active-100)
}


.wrapper-for-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  width:100%;
  height:100%;
  position: absolute;
  z-index: 2;
  top:0;
  background: var(--black-20);
}

.fade-item {
  opacity: 0;
  animation: fadeIn 0.5s ease-in-out forwards;
  animation-delay: var(--delay, 0s);
}
</style>
